import React, { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useInventoryStore } from '../../../store/inventoryStore';
import { useWarehouseStore } from '../../../store/warehouseStore';
import { useStockMovementStore, generatePendingId } from '../../../store/stockMovementStore';
import { colours, StepNav, Select, Input, FieldLabel } from './shared';

const StepIndicator: React.FC<{ steps: string[]; current: number }> = ({ steps, current }) => (
  <div style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '8px 16px 0', boxSizing: 'border-box', flexShrink: 0 }}>
    {steps.map((label, i) => {
      const done = i < current; const active = i === current;
      return (
        <React.Fragment key={i}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:22, height:22, borderRadius:'50%', fontSize:12, fontWeight:700, backgroundColor: done ? '#16a34a' : active ? '#3b82f6' : '#374151', color:'#fff', flexShrink:0 }}>{done ? '✓' : i + 1}</span>
            <span style={{ fontSize:13, fontWeight:500, whiteSpace:'nowrap', color: active ? colours.text : done ? '#6b7280' : '#4b5563' }}>{label}</span>
          </div>
          {i < steps.length - 1 && <div style={{ flex:1, height:1, margin:'0 16px', backgroundColor: done ? '#16a34a' : colours.border }} />}
        </React.Fragment>
      );
    })}
  </div>
);

interface Props { onQueued: () => void; onOpenReview?: () => void; }

const STEPS = ['Select Units', 'Destination'];

const ReturnStockForm: React.FC<Props> = ({ onQueued, onOpenReview }) => {
  const { items, stockUnits, fetchItems, fetchStockUnits } = useInventoryStore();
  const { warehouses, storageAreasByWarehouse, defaultWarehouseId } = useWarehouseStore();
  const { addPending } = useStockMovementStore();

  const [step, setStep]             = useState(0);
  const [search, setSearch]         = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [reference, setReference]   = useState('');
  const [notes, setNotes]           = useState('');
  const [condition, setCondition]   = useState<'new' | 'used'>('used');

  // Destination
  const defaultWh = warehouses.find(w => w.id === defaultWarehouseId) ?? warehouses[0];
  const [warehouseId, setWarehouseId]     = useState(defaultWh?.id ?? '');
  const [storageAreaId, setStorageAreaId] = useState('');

  useEffect(() => { fetchItems(); fetchStockUnits(); }, [fetchItems, fetchStockUnits]);
  useEffect(() => { if (!warehouseId && defaultWh) setWarehouseId(defaultWh.id); }, [defaultWh, warehouseId]);

  const itemById = useMemo(() => new Map(items.map(i => [i.id, i])), [items]);
  const areas = useMemo(() =>
    warehouseId ? (storageAreasByWarehouse[warehouseId] ?? []) : [],
    [storageAreasByWarehouse, warehouseId]
  );

  // Auto-select first storage area when warehouse changes
  useEffect(() => {
    if (areas.length > 0 && !areas.find(a => a.id === storageAreaId)) {
      setStorageAreaId(areas[0].id);
    }
  }, [areas, storageAreaId]);

  // Only show checked-out units
  const checkedOutUnits = useMemo(() =>
    stockUnits.filter(u => u.status === 'checked out'),
    [stockUnits]
  );

  const filtered = useMemo(() => {
    if (!search) return checkedOutUnits;
    const q = search.toLowerCase();
    return checkedOutUnits.filter(u => {
      const item = itemById.get(u.itemId);
      return [item?.name, item?.sku, u.serialMac, u.reference, u.owner, String(u.unitId)]
        .filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [checkedOutUnits, search, itemById]);

  const toggle = (id: string) =>
    setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAll = () =>
    setSelectedIds(selectedIds.size === filtered.length ? new Set() : new Set(filtered.map(u => u.id)));

  const handleQueue = () => {
    addPending({
      type: 'return_stock',
      id: generatePendingId(),
      unitIds: Array.from(selectedIds),
      warehouseId,
      storageAreaId: storageAreaId || null,
      condition,
      reference,
      notes,
    });
    setSelectedIds(new Set());
    setSearch('');
    setReference('');
    setNotes('');
    setCondition('used');
    setStep(0);
    onQueued();
  };

  const { border, cardBg, headerBg, muted, text, inputBg, rowHover } = colours;

  return (
    <div className="flex-1 flex flex-col min-h-0" style={{ backgroundColor: colours.pageBg }}>
      <StepIndicator steps={STEPS} current={step} />

      {/* ── Step 1: Select checked-out units ── */}
      {step === 0 && (
        <div className="flex-1 flex flex-col min-h-0 px-4 py-4 gap-3">
          {/* Search */}
          <div className="relative flex-shrink-0" style={{ maxWidth: 340 }}>
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: muted }} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, serial, reference, owner…"
              className="w-full text-sm pl-8 pr-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
              style={{ backgroundColor: inputBg, borderColor: border, color: text }}
            />
          </div>

          {checkedOutUnits.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-sm" style={{ color: muted }}>No checked-out units found.</p>
            </div>
          ) : (
            <div className="flex-1 overflow-auto rounded" style={{ border: `1px solid ${border}` }}>
              <table className="w-full">
                <thead className="sticky top-0" style={{ backgroundColor: headerBg }}>
                  <tr style={{ borderBottom: `1px solid ${border}` }}>
                    <th className="px-3 py-2 w-8">
                      <input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0} onChange={toggleAll} />
                    </th>
                    {['Item', 'Unit ID', 'Serial/MAC', 'Owner', 'Reference'].map(h => (
                      <th key={h} className="px-3 py-2 text-left text-xs uppercase tracking-wide font-medium" style={{ color: muted }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody style={{ backgroundColor: cardBg }}>
                  {filtered.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-8 text-center text-sm" style={{ color: muted }}>No results.</td></tr>
                  )}
                  {filtered.map((unit, idx) => {
                    const item = itemById.get(unit.itemId);
                    const sel  = selectedIds.has(unit.id);
                    return (
                      <tr
                        key={unit.id}
                        className="cursor-pointer transition-colors"
                        style={{
                          borderBottom: idx < filtered.length - 1 ? `1px solid ${border}` : undefined,
                          backgroundColor: sel ? '#1d2535' : undefined,
                        }}
                        onClick={() => toggle(unit.id)}
                        onMouseEnter={e => { if (!sel) (e.currentTarget as HTMLElement).style.backgroundColor = rowHover; }}
                        onMouseLeave={e => { if (!sel) (e.currentTarget as HTMLElement).style.backgroundColor = ''; }}
                      >
                        <td className="px-3 py-1.5"><input type="checkbox" checked={sel} onChange={() => toggle(unit.id)} onClick={e => e.stopPropagation()} /></td>
                        <td className="px-3 py-1.5 text-sm font-medium" style={{ color: text }}>{item?.name ?? '—'}</td>
                        <td className="px-3 py-1.5">
                          <span className="inline-flex items-center justify-center text-xs font-medium rounded px-1.5 py-0.5" style={{ backgroundColor: '#374151', color: '#d1d5db' }}>
                            {unit.unitId}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-sm" style={{ color: unit.serialMac ? text : muted }}>{unit.serialMac || '—'}</td>
                        <td className="px-3 py-1.5 text-sm" style={{ color: unit.owner ? text : muted }}>{unit.owner || '—'}</td>
                        <td className="px-3 py-1.5 text-sm" style={{ color: unit.reference ? text : muted }}>{unit.reference || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {selectedIds.size > 0 && (
            <p className="text-sm flex-shrink-0" style={{ color: muted }}>{selectedIds.size} unit{selectedIds.size > 1 ? 's' : ''} selected</p>
          )}
        </div>
      )}

      {/* ── Step 2: Destination + notes ── */}
      {step === 1 && (
        <div className="flex-1 overflow-auto px-4 py-4">
          <div
            className="max-w-md rounded p-5 flex flex-col gap-4"
            style={{ backgroundColor: cardBg, border: `1px solid ${border}` }}
          >
            <p className="text-sm" style={{ color: muted }}>
              Where are these {selectedIds.size} unit{selectedIds.size > 1 ? 's' : ''} being returned to?
            </p>
            <Select label="Warehouse" value={warehouseId} onChange={e => { setWarehouseId(e.target.value); setStorageAreaId(''); }} wFull>
              <option value="">Select warehouse…</option>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </Select>
            <Select label="Storage Area" value={storageAreaId} onChange={e => setStorageAreaId(e.target.value)} wFull disabled={areas.length === 0}>
              {areas.length === 0
                ? <option value="">No storage areas</option>
                : areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)
              }
            </Select>

            <div>
              <FieldLabel>Mark stock as new or used</FieldLabel>
              <div className="mt-1.5 flex rounded overflow-hidden" style={{ border: `1px solid ${border}` }}>
                {(['used', 'new'] as const).map(opt => {
                  const active = condition === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setCondition(opt)}
                      className="flex-1 text-sm font-medium py-2 transition-colors capitalize"
                      style={{
                        backgroundColor: active ? '#2563eb' : inputBg,
                        color: active ? '#fff' : muted,
                      }}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>

            <Input label="Reference (optional)" placeholder="Job number, return note…" value={reference} onChange={e => setReference(e.target.value)} wFull />
            <Input label="Notes (optional)" placeholder="Any notes…" value={notes} onChange={e => setNotes(e.target.value)} wFull />
          </div>
        </div>
      )}

      <StepNav
        onBack={step > 0 ? () => setStep(0) : undefined}
        onNext={step === 0 ? () => setStep(1) : undefined}
        onQueue={step === 1 ? handleQueue : undefined}
        onReview={onOpenReview}
        nextDisabled={selectedIds.size === 0}
        queueDisabled={!warehouseId}
        queueLabel="Add to Queue ✓"
      />
    </div>
  );
};

export default ReturnStockForm;
